import { Box, useTheme, CircularProgress, Typography, styled } from "@mui/material";
import ChatWindow from "./session-components/ChatWindow";
import VideoSession from "./session-components/VideoSession";
import SessionAction from "./session-components/SessionAction";
import { useEffect, useRef, useCallback, useState } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import useSession from "hooks/call-queue/useSession";
import useApi from "hooks/useApi";
import useFirebase from "hooks/useFirebase";
import { CallStatus } from "types/auth";
import { useNavigate } from "react-router";
import { RING_TIMEOUT_MS } from "utils/callTiming";

// Video call area loading overlay component
const VideoCallLoadingOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(1px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
}));

const VideoCall = () => {
    const theme = useTheme();
    const { 
        session, 
        client,
        setClient, 
        localAudioTrack, 
        setLocalAudioTrack, 
        setRemoteVideo,
        updateSessionState 
    } = useSession();
    const { base64Decode, user, cancelCall, showToast } = useApi();
    const { updateCallStatus, addCallHistoryEntry, addCallToUser, addSessionUsers, updateRecipients, updateCallSupportInitiatedBy } = useFirebase();
    
    // Keep track of initialization to prevent duplicate calls
    const initializingRef = useRef(false);
    const initializedSessionsRef = useRef(new Set<string>());
    // Calls we have already promoted RECEIVED -> IN_PROGRESS as the caller.
    const promotedRef = useRef<string | null>(null);
    // The remote video track currently rendered into #remote-video, so we can
    // stop() it before rendering another. Held in a ref, not state, because the
    // Agora callbacks below would otherwise close over a stale value.
    const playedVideoRef = useRef<any>(null);
    
    // Global loading state for session ending
    const [isEndingSession, setIsEndingSession] = useState(false);
    // Chrome refuses audio playback on a page that has had no user gesture.
    const [autoplayBlocked, setAutoplayBlocked] = useState(false);

    // Memoized client creation to avoid recreating
    const createAgoraClient = useCallback(() => {
        if (!client) {
            console.log("VideoCall: Creating new Agora client");
            return AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
        }
        return client;
    }, [client]);

    // Subscribe AND render here — this is the only `user-published` handler.
    //
    // VideoSession used to register a second one that did the rendering, and both
    // called removeAllListeners(), so whichever mounted last silently disabled the
    // other. Worse, both were attached AFTER join(), which drops the burst of
    // `user-published` events Agora replays for peers already in the channel —
    // i.e. every rejoin after a page refresh, where the app is already publishing.
    // That is why a refreshed dashboard never got the video back.
    const setupClientEventListeners = useCallback((agoraClient: any) => {
        // Remove existing listeners to avoid duplicates
        agoraClient.removeAllListeners();
        
        // play() APPENDS a <video> under the container and only stop() removes it
        // (per the SDK's own docs). Leaving the old one behind is why a camera
        // off/on cycle left the dashboard showing a frozen frame: the dead
        // <video> stayed stacked on top of the freshly published one.
        const stopPlayedVideo = () => {
            try {
                playedVideoRef.current?.stop();
            } catch (error) {
                console.warn("VideoCall: error stopping previous remote video:", error);
            }
            playedVideoRef.current = null;
        };

        agoraClient.on("user-published", async (remoteUser: any, mediaType: string) => {
            try {
                await agoraClient.subscribe(remoteUser, mediaType);

                if (mediaType === "video" && remoteUser.videoTrack) {
                    stopPlayedVideo();
                    playedVideoRef.current = remoteUser.videoTrack;
                    setRemoteVideo(remoteUser.videoTrack);
                    // fit: "contain" — the phone publishes a portrait stream into a
                    // landscape tile. Agora's default "cover" crops it to fill, which
                    // is the "way too zoomed in" view on the dashboard. Agora writes
                    // object-fit inline on the <video>, so the CSS rule alone loses.
                    try {
                        remoteUser.videoTrack.play("remote-video", { fit: "contain" });
                    } catch (playError) {
                        // The container only exists once the call is IN_PROGRESS;
                        // VideoSession re-attaches the track when it mounts.
                        console.warn("VideoCall: remote video container not ready yet", playError);
                    }
                } else if (mediaType === "audio" && remoteUser.audioTrack) {
                    remoteUser.audioTrack.play();
                }
            } catch (subscribeError) {
                console.error(`Failed to subscribe to ${mediaType}:`, subscribeError);
            }
        });

        agoraClient.on("user-unpublished", (remoteUser: any, mediaType: string) => {
            if (mediaType === "video") {
                stopPlayedVideo();
                setRemoteVideo(null);
            }
        });
        
        agoraClient.on("user-left", (remoteUser: any) => {
            stopPlayedVideo();
            setRemoteVideo(null);
        });
        
        // Only log critical errors
        agoraClient.on('exception', (e: any) => {
            console.error(`VideoCall Exception:`, e);
        });
    }, [setRemoteVideo]);

    const joinCall = useCallback(async () => {
        if (!session || !user) return;
        
        const sessionId = session.transaction;
        if (!sessionId) return;

        // Check if this session is already initialized
        if (initializedSessionsRef.current.has(sessionId)) {
            return;
        }

        // Prevent concurrent initialization
        if (initializingRef.current) {
            return;
        }

        initializingRef.current = true;

        try {
            // The outgoing caller joins the channel while it is still ringing so
            // the dashboard shows a real "call happening" UI (local video, waiting
            // for the callee) instead of a blank page until someone answers.
            const isOutgoingCaller = session.callDirection === 'INBOUND' && session.from === user.uid;
            const callerRinging = isOutgoingCaller && (session.status == CallStatus.CALLING || session.status == CallStatus.RECEIVING);
            // Holding a token means we are a participant of this very session. On a
            // page refresh the call is IN_PROGRESS and `recipients` has already been
            // cleared (that is how other agents stop ringing), so the recipients test
            // alone left the reloaded tab sitting in the call without ever rejoining.
            const holdsToken = Boolean(session.session?.tokens?.some(t => t.uid == user.uid));
            const rejoining = session.status == CallStatus.IN_PROGRESS &&
                (session.recipients?.includes(user.uid) || holdsToken);
            if (session.status == CallStatus.RECEIVED || rejoining || callerRinging) {
                
                // For CHAT sessions, skip Agora setup entirely
                if (session?.sessionType === "CHAT") {
                    console.log("VideoCall: CHAT session detected, skipping Agora setup");
                    
                    // Update call status for chat sessions
                    if (session.status == CallStatus.RECEIVED) {
                        updateCallStatus(session.transaction!, CallStatus.IN_PROGRESS);
                        updateCallSupportInitiatedBy(session.transaction!, user.uid);
                        addCallHistoryEntry(session.transaction!, { 
                            status: CallStatus.IN_PROGRESS, 
                            datetime: new Date().toISOString(), 
                            user: user.uid 
                        });
                    }
                    
                    addCallToUser(session.transaction!, user.uid, CallStatus.IN_PROGRESS, "SUPPORT");
                    updateRecipients(session.transaction!, []);
                    addSessionUsers(session.transaction!, user.uid);
                    addCallHistoryEntry(session.transaction!, { 
                        status: CallStatus.SUPPORT_JOINED, 
                        datetime: new Date().toISOString(), 
                        user: user.uid 
                    });

                    // Mark this session as initialized
                    initializedSessionsRef.current.add(sessionId);
                    return;
                }
                
                let userTokenObj = session.session?.tokens.find(token => token.uid == user.uid);
                
                if (!userTokenObj) {
                    console.error("VideoCall: User token not found");
                    return;
                }

                // Use existing client or create new one
                let agoraClient = client || createAgoraClient();
                
                // Listeners first: Agora replays `user-published` for everyone
                // already in the channel as part of join(), so a handler attached
                // afterwards misses them.
                setupClientEventListeners(agoraClient);

                // The client lives in VideoSessionContext and survives between calls,
                // but EVERY call gets its own Agora channel (test0097_21, _22, ...)
                // and its own uid. A client still CONNECTED to the previous call's
                // channel made the "already connected" check below skip join()
                // altogether, leaving the dashboard sitting in the old, empty room
                // while the driver published into the new one: no remote video, no
                // audio, and a refresh "fixing" it only because that threw the stale
                // client away. Leave the wrong channel before joining the right one.
                const targetChannel = session.session?.channelName;
                if (agoraClient.connectionState === "CONNECTED" && agoraClient.channelName !== targetChannel) {
                    console.log(`VideoCall: leaving stale channel ${agoraClient.channelName} to join ${targetChannel}`);
                    try {
                        await agoraClient.leave();
                    } catch (leaveError) {
                        console.warn("VideoCall: error leaving stale channel:", leaveError);
                    }
                    // The old channel's mic track is no longer published anywhere;
                    // drop it so a fresh one is created and published below.
                    if (localAudioTrack) {
                        try { localAudioTrack.stop(); localAudioTrack.close(); } catch (e) { /* noop */ }
                        setLocalAudioTrack(null);
                    }
                }

                // Only join if not already connected
                if (agoraClient.connectionState !== "CONNECTED") {
                    console.log("VideoCall: 🔗 Joining Agora channel...");
                    
                    await agoraClient.join(
                        base64Decode(session.session?.appId!),
                        session.session?.channelName!,
                        userTokenObj?.token!,
                        Number(userTokenObj?.uidNumber)
                    );
                    
                    console.log("VideoCall: ✅ Successfully joined Agora channel");
                    
                    // Update session state
                    updateSessionState({
                        isConnected: true,
                        channelName: session.session?.channelName,
                        appId: session.session?.appId,
                        userToken: userTokenObj?.token,
                        uidNumber: Number(userTokenObj?.uidNumber)
                    });
                }

                // Set client in context if not already set
                if (!client) {
                    setClient(agoraClient);
                }

                // Create and publish audio track if not already exists
                if (!localAudioTrack) {
                    try {
                        const microphoneTrack = await AgoraRTC.createMicrophoneAudioTrack({
                            AEC: true, // Acoustic Echo Cancellation
                            AGC: true, // Automatic Gain Control
                            ANS: true  // Automatic Noise Suppression
                        });
                        setLocalAudioTrack(microphoneTrack);
                        
                        if (agoraClient.connectionState === "CONNECTED") {
                            await agoraClient.publish([microphoneTrack]);
                        }
                    } catch (audioError) {
                        console.error("VideoCall: Error creating/publishing audio track:", audioError);
                    }
                } else {
                    // Re-publish existing audio track only if not already published
                    if (agoraClient.connectionState === "CONNECTED") {
                        try {
                            // Check if the track is already published to avoid disrupting mute state
                            const publishedTracks = agoraClient.localTracks;
                            const isAlreadyPublished = publishedTracks.some(track => track === localAudioTrack);
                            
                            if (!isAlreadyPublished) {
                                await agoraClient.publish([localAudioTrack]);
                                console.log("VideoCall: Re-published existing audio track");
                            } else {
                                console.log("VideoCall: Audio track already published, maintaining current state");
                            }
                        } catch (publishError) {
                            console.warn("VideoCall: Failed to re-publish audio track:", publishError);
                        }
                    }
                }

                // Update call status if needed
                if (session.status == CallStatus.RECEIVED) {
                    updateCallStatus(session.transaction!, CallStatus.IN_PROGRESS);
                    updateCallSupportInitiatedBy(session.transaction!, user.uid);
                    addCallHistoryEntry(session.transaction!, { 
                        status: CallStatus.IN_PROGRESS, 
                        datetime: new Date().toISOString(), 
                        user: user.uid 
                    });
                }
                
                // These four writes mean "an agent has ANSWERED this call". They ran
                // unconditionally, which was wrong on two paths:
                //
                //  - as the outgoing caller we join while the callee's phone is still
                //    ringing, so this marked us IN_PROGRESS and — worse — cleared
                //    `recipients` mid-ring, erasing who the call was actually for;
                //  - on a refresh mid-call it appended a second identical entry to
                //    `to` and to `session.joinees` every time the page reloaded.
                //
                // Only a real answer (status was RECEIVED) should write them.
                // Only claim to have joined if we really are in THIS call's channel.
                // Written unconditionally, SUPPORT_JOINED reported success even when
                // join() had been skipped because the client was still in a previous
                // call's room - which made the call records look healthy while no
                // media could possibly flow, and cost a lot of debugging time.
                const reallyJoined = agoraClient.connectionState === "CONNECTED"
                    && agoraClient.channelName === session.session?.channelName;
                if (!reallyJoined) {
                    console.error("VideoCall: not in the expected channel after join", {
                        expected: session.session?.channelName,
                        actual: agoraClient.channelName,
                        state: agoraClient.connectionState
                    });
                }

                addCallToUser(session.transaction!, user.uid, CallStatus.IN_PROGRESS, "SUPPORT");
                addSessionUsers(session.transaction!, user.uid);
                addCallHistoryEntry(session.transaction!, { 
                    status: CallStatus.SUPPORT_JOINED, 
                    datetime: new Date().toISOString(), 
                    user: user.uid 
                });

                // But only clear the recipient list on a genuine ANSWER. As the
                // outgoing caller we join while the callee's phone is still
                // ringing, and clearing it there erased who the call was for.
                if (session.status == CallStatus.RECEIVED) {
                    updateRecipients(session.transaction!, []);
                }

                // Mark this session as initialized
                initializedSessionsRef.current.add(sessionId);
            }
        } catch (error) {
            console.error("VideoCall: ❌ Error during call initialization:", error);
        } finally {
            initializingRef.current = false;
        }
    }, [session, user, client, localAudioTrack, createAgoraClient, setupClientEventListeners, setClient, setLocalAudioTrack, updateSessionState, base64Decode, updateCallStatus, updateCallSupportInitiatedBy, addCallHistoryEntry, addCallToUser, updateRecipients, addSessionUsers]);

    useEffect(() => {
        if (session) {
            joinCall();
        }
    }, [session, joinCall]);

    // Remote AUDIO is the only thing here that browsers gate behind a user
    // gesture - video is muted by definition and always plays. So a call loaded
    // without a click (a reload straight onto /session/...) renders video
    // perfectly and stays silent, with play() rejecting where nobody sees it.
    // Agora reports it here; surface it and offer a click to recover.
    useEffect(() => {
        AgoraRTC.onAutoplayFailed = () => {
            console.warn('VideoCall: browser blocked audio autoplay - user gesture required');
            setAutoplayBlocked(true);
        };
        return () => { AgoraRTC.onAutoplayFailed = undefined; };
    }, []);

    const enableSound = useCallback(() => {
        const c: any = client;
        (c?.remoteUsers || []).forEach((u: any) => {
            try { u.audioTrack?.play(); } catch (e) { console.warn('enableSound failed', e); }
        });
        setAutoplayBlocked(false);
    }, [client]);

    // Reconcile subscriptions against what the remote is actually publishing.
    //
    // Agora fires `user-published` exactly ONCE per track and never replays it.
    // Miss it - listeners re-attached mid-join, a throttled background tab, any
    // race during setup - and that track is never subscribed, with no second
    // chance. That is how a call ends up with video but no audio: the two
    // publishes arrive moments apart and only one lands.
    //
    // `hasAudio`/`hasVideo` is what they are sending; `audioTrack`/`videoTrack`
    // is what we have subscribed to. Any disagreement is a dropped event, and
    // fixing it here makes the whole path self-healing instead of one-shot.
    useEffect(() => {
        if (!client) return;

        const reconcile = async () => {
            const c: any = client;
            if (c.connectionState !== "CONNECTED") return;

            for (const u of (c.remoteUsers || [])) {
                try {
                    if (u.hasAudio && !u.audioTrack) {
                        console.warn('VideoCall: recovering dropped AUDIO subscription from', u.uid);
                        await c.subscribe(u, "audio");
                    }
                    if (u.audioTrack && !u.audioTrack.isPlaying) {

                        u.audioTrack.play();
                    }
                    if (u.hasVideo && !u.videoTrack) {
                        console.warn('VideoCall: recovering dropped VIDEO subscription from', u.uid);
                        await c.subscribe(u, "video");
                    }
                    if (u.videoTrack && !u.videoTrack.isPlaying && document.getElementById("remote-video")) {

                        setRemoteVideo(u.videoTrack);
                        u.videoTrack.play("remote-video", { fit: "contain" });
                    }
                } catch (error) {
                    console.warn('VideoCall: subscription recovery failed for', u.uid, error);
                }
            }
        };

        reconcile();
        const id = setInterval(reconcile, 2000);
        return () => clearInterval(id);
    }, [client, setRemoteVideo]);

    const navigate = useNavigate();
    // Calls we have already given up on, so the timer cannot fire twice.
    const timedOutRef = useRef<string | null>(null);

    // Give up on an unanswered outgoing call.
    //
    // There was no timeout on this path at all: OutgoingCallOverlay owns the
    // 45s timer but only runs when `outgoingCallId` is set, and CallUserDialog
    // navigates straight to /session/{id} without setting it. So a call nobody
    // answered rang indefinitely - one was still CALLING after two minutes.
    //
    // The deadline is measured from `initiated`, not from mount, so refreshing
    // the page does not silently restart the clock.
    useEffect(() => {
        if (!session || !user) return;
        const isOutgoingCaller = session.callDirection === 'INBOUND' && session.from === user.uid;
        const ringing = session.status === CallStatus.CALLING || session.status === CallStatus.RECEIVING;
        if (!isOutgoingCaller || !ringing) return;
        if (timedOutRef.current === session.transaction) return;

        const elapsed = Date.now() - (session.initiated || Date.now());
        const remaining = Math.max(0, RING_TIMEOUT_MS - elapsed);

        const timer = setTimeout(async () => {
            timedOutRef.current = session.transaction!;
            try {
                await updateCallStatus(session.transaction!, CallStatus.MISSED_CALL);
                addCallHistoryEntry(session.transaction!, {
                    status: CallStatus.MISSED_CALL,
                    datetime: new Date().toISOString(),
                    user: user.uid
                });
                // Tell the device to dismiss its CallKit UI rather than ring on.
                try { await cancelCall(session.transaction!); } catch (e) { /* best effort */ }
                showToast('No answer', 'info');
                navigate('/');
            } catch (error) {
                console.error('VideoCall: failed to time out unanswered call', error);
            }
        }, remaining);

        return () => clearTimeout(timer);
    }, [session, user, updateCallStatus, addCallHistoryEntry, cancelCall, showToast, navigate]);

    // Promote the call to IN_PROGRESS once the callee answers.
    //
    // As the outgoing caller we join the channel while the phone is still
    // ringing, and that join marks the session initialized — so when the app
    // answers and the status flips to RECEIVED, joinCall early-returns on its
    // own guard and never reaches the branch that writes IN_PROGRESS. The call
    // then sat at RECEIVED forever, and since every bit of remote-video
    // rendering keys on IN_PROGRESS the dashboard showed "Connecting..." with no
    // video even though both sides were in the Agora channel.
    //
    // Kept out of joinCall deliberately: joining and reacting to being answered
    // are different events, and conflating them is what caused this.
    useEffect(() => {
        if (!session || !user) return;
        const isOutgoingCaller = session.callDirection === 'INBOUND' && session.from === user.uid;
        if (!isOutgoingCaller || session.status !== CallStatus.RECEIVED) return;
        if (promotedRef.current === session.transaction) return;

        promotedRef.current = session.transaction!;
        updateCallStatus(session.transaction!, CallStatus.IN_PROGRESS);
        updateCallSupportInitiatedBy(session.transaction!, user.uid);
        addCallHistoryEntry(session.transaction!, {
            status: CallStatus.IN_PROGRESS,
            datetime: new Date().toISOString(),
            user: user.uid
        });
    }, [session, user, updateCallStatus, updateCallSupportInitiatedBy, addCallHistoryEntry]);

    // Reset session tracking when call ends
    useEffect(() => {
        if (session?.status === CallStatus.COMPLETED) {
            const sessionId = session.transaction;
            if (sessionId) {
                initializedSessionsRef.current.delete(sessionId);
            }
            if (promotedRef.current === session.transaction) {
                promotedRef.current = null;
            }
            initializingRef.current = false;
        }
    }, [session?.status, session?.transaction]);

    // Optimized cleanup only when truly needed - Skip for CHAT sessions
    useEffect(() => {
        return () => {
            // Skip cleanup for CHAT sessions as they don't use Agora
            if (session?.sessionType === "CHAT") {
                return;
            }
            
            // Only cleanup if session is completed or component is unmounting
            if (session?.status === CallStatus.COMPLETED || 
                session?.status === CallStatus.USER_LEFT ||
                session?.status === CallStatus.MANUAL_DISCONNECT) {
                
                if (localAudioTrack) {
                    try {
                        localAudioTrack.stop();
                        localAudioTrack.close();
                    } catch (error) {
                        console.warn("VideoCall: Error stopping audio track:", error);
                    }
                }
                if (client && client.connectionState === "CONNECTED") {
                    try {
                        client.leave();
                    } catch (error) {
                        console.warn("VideoCall: Error leaving Agora channel:", error);
                    }
                }
            }
        };
    }, [session?.status, session?.sessionType, localAudioTrack, client]);

    // Modern video conferencing layout
    return (
        <Box sx={{
            height: '94vh',
            backgroundColor: '#f5f5f5', // Match skeleton background
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            padding: '16px', // Add padding like skeleton
            gap: '16px', // Add gap like skeleton
            position: 'relative' // Enable absolute positioning for overlay
        }}>
            {/* Main Content Area */}
            <Box sx={{ 
                flex: 1, 
                display: 'flex',
                gap: '16px', // Add gap between video and chat
                minHeight: 0
            }}>
                {/* Video Area */}
                {session?.sessionType !== "CHAT" ? (
                    <Box sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        backgroundColor: '#1a1a1a', // Match skeleton dark background
                        borderRadius: '16px', // Match skeleton border radius
                        overflow: 'hidden',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)' // Match skeleton shadow
                    }}>
                        <VideoSession />
                        
                        {/* Floating Controls */}
                        <Box sx={{
                            position: 'absolute',
                            bottom: 24,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 10
                        }}>
                            {session?.status !== CallStatus.COMPLETED && <SessionAction onEndingSession={setIsEndingSession} />}
                        </Box>
                    </Box>
                ) : null}

                {/* Chat Panel */}
                <Box sx={{
                    width: session?.sessionType !== "CHAT" ? '400px' : '100%', // Fixed width like skeleton
                    maxWidth: session?.sessionType !== "CHAT" ? '400px' : 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: '0', // Remove border radius to match skeleton
                    boxShadow: 'none', // Remove shadow to match skeleton
                    border: 'none', // Remove border to match skeleton
                    position: 'relative'
                }}>
                    <ChatWindow onEndingSession={setIsEndingSession} />
                </Box>
            </Box>

            {autoplayBlocked && (
                <Box
                    onClick={enableSound}
                    sx={{
                        position: 'absolute', top: 24, left: '50%', transform: 'translateX(-50%)',
                        zIndex: 10000, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1,
                        backgroundColor: 'warning.main', color: '#fff',
                        px: 2, py: 1, borderRadius: '20px',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.3)'
                    }}
                >
                    <Typography variant="body2" fontWeight={600}>
                        Click to enable sound
                    </Typography>
                </Box>
            )}

            {/* Video call area Loading Overlay */}
            {isEndingSession && (
                <VideoCallLoadingOverlay>
                    <CircularProgress color="inherit" size={60} />
                    <Typography variant="h6" color="white" sx={{ mt: 3, fontWeight: 500 }}>
                        Ending session, please wait...
                    </Typography>
                </VideoCallLoadingOverlay>
            )}
        </Box>
    )
}

export default VideoCall;